import React from "react";
import {
  filter,
  includes,
  map,
  mapValues,
  omitBy,
  pickBy,
  reject,
  size,
  some,
} from "lodash";
import styled from "styled-components";
import {
  FontSize,
  Spacing,
  content,
  dominant,
  form,
  transparentize,
} from "@glossy";
import { Facet } from "@linkfluence/models/facet";
import { Locale } from "@linkfluence/models/locale";
import { natural } from "@tootools/sort";
import { useContextualAccounts } from "@radarly/core/context/use-cases";
import { useFeature } from "@radarly/core/features/use-cases";
import { tr } from "@radarly/i18n";
import {
  Categories,
  FilterCategories,
  FullFacet,
} from "@radarly/models/facets";

interface IProps {
  onClick: (field: Facet) => void;
  facets: Record<string, FullFacet>;
  categories: Record<keyof typeof Categories, Facet[]>;
  usedFacets: string[];
  hiddenEmptyFacets?: Facet[];
  locale?: Locale;
  className?: string;
}

const COLUMNS = [
  ["basic", "team"],
  ["text", "image", "posts"],
  ["author", "reviews", "misc"],
];

const withUsed = (facet, usedFacets) => ({
  ...facet,
  used: includes(usedFacets, facet.id),
});

const formatFilterGrid = (
  allFacets: Record<string, FullFacet>,
  categories: Record<keyof typeof Categories, Facet[]>,
  usedFacets: string[]
): FilterCategories => {
  const selectedFullFacets = mapValues(categories, (category) =>
    map(
      filter(category, (facet) =>
        some(reject(allFacets, { custom: true }), { id: facet })
      ),
      (facet) => withUsed(allFacets[facet], usedFacets)
    )
  );
  return {
    ...selectedFullFacets,
    team: map(
      [
        ...selectedFullFacets.team,
        ...natural(filter(allFacets, { custom: true }), "label"),
      ],
      (facet) => withUsed(facet, usedFacets)
    ),
  };
};

const hideFacetWhenEmpty = (facetName: FullFacet["id"]): boolean =>
  includes(
    [
      Facet.Corpus,
      Facet.ImageRecognitionLogos,
      Facet.ImageRecognitionPersonIdentifications,
      Facet.InfluencersTags,
      Facet.GeoTags,
      Facet.GeoLocations,
    ],
    facetName
  );

const getColumns = (facets: FilterCategories) => {
  const result = COLUMNS.map((column) =>
    column.filter((facet) => facets[facet].length > 0)
  );
  return result.filter((res) => res.length > 0);
};

const Root = styled.div`
  margin: ${Spacing.Large} ${Spacing.Large} 0 0;
  display: flex;
  align-content: stretch;
`;

const Title = styled.div`
  font-size: ${FontSize.Large};
  color: ${content};
  margin-bottom: ${Spacing.ExtraSmall};
  padding-left: ${Spacing.Large};
`;

const Filter = styled.div<{ used: boolean }>`
  color: ${(props) =>
    props.used ? transparentize(0.5, form(props, "primary")) : dominant};
  cursor: pointer;
  pointer-events: ${(props) => props.used && "none"};
  line-height: 20px;
  padding: ${Spacing.Micro} 0 ${Spacing.Micro} ${Spacing.Large};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  &:hover {
    background-color: ${(props) =>
      !props.used && transparentize(0.85, dominant(props))};
  }
`;

const Section = ({
  id,
  label,
  locale,
  formattedFacets,
  onClick,
  className,
}: {
  id: string;
  label: string;
  locale: Locale;
  formattedFacets: FilterCategories;
  onClick: (field: Facet) => void;
  className?: string;
}) => (
  <section className={className}>
    <Title>{tr(locale, label)} </Title>
    {formattedFacets[id] &&
      map(formattedFacets[id], ({ id, label, used }) => (
        <Filter
          used={used}
          onClick={() => (used ? null : onClick(id))}
          key={id}
        >
          {label}
        </Filter>
      ))}
  </section>
);

const StyledSection = styled(Section)`
  margin-bottom: ${Spacing.Large};
`;

const Column = styled.div`
  flex-basis: 33.33%;
  width: 33.33%;
`;

const RadarlyComponentsFiltersList = ({
  onClick,
  facets,
  categories,
  usedFacets,
  locale,
  hiddenEmptyFacets = [],
  className,
}: IProps) => {
  const withAuthorType = useFeature("facet_author_type");
  const accounts = useContextualAccounts();
  const all_facets: Record<string, FullFacet> = omitBy(
    facets,
    (facet, facetName) => {
      if (hiddenEmptyFacets.length > 0) {
        if (hiddenEmptyFacets.includes(facetName as Facet)) {
          return !size(facet.values);
        }
        return false;
      }
      if (hideFacetWhenEmpty(facetName)) {
        return !size(facet.values);
      }
      if (
        facet.id === Facet.AuthorType &&
        (!withAuthorType || accounts.length === 0)
      ) {
        return true;
      }
      return false;
    }
  );
  const formattedFacets = formatFilterGrid(all_facets, categories, usedFacets);
  const columns = getColumns(formattedFacets);
  return (
    <Root className={className}>
      {map(columns, (column, id) => (
        <Column key={id}>
          {map(
            pickBy(Categories, (_, id: string) => includes(column, id)),
            (label: string, id: string) => (
              <StyledSection
                key={id}
                id={id}
                onClick={onClick}
                locale={locale}
                label={label}
                formattedFacets={formattedFacets}
              />
            )
          )}
        </Column>
      ))}
    </Root>
  );
};

export default RadarlyComponentsFiltersList;
